import { useState } from 'react';

import { Publication, PublicationDraft, PublicationWindow } from './types';

import { defaultPublicationDraft } from './giftFormUtils';

interface Options {
    publications?: Publication[];
    isNewGift: boolean;
    onCreatePublication?: (window: PublicationWindow) => Promise<Publication>;
    onUpdatePublication?: (
        publicationId: string,
        window: PublicationWindow,
    ) => Promise<void>;
    onDeletePublication?: (publicationId: string) => Promise<void>;
}

/** Returns the ISO window for a draft, or undefined if it is incomplete. */
function toWindow(draft: PublicationDraft): PublicationWindow | undefined {
    if (!draft.startDate || !draft.endDate) return undefined;
    return {
        startDate: new Date(draft.startDate).toISOString(),
        endDate: new Date(draft.endDate).toISOString(),
    };
}

export function usePublicationDrafts({
    publications,
    isNewGift,
    onCreatePublication,
    onUpdatePublication,
    onDeletePublication,
}: Options) {
    const [drafts, setDrafts] = useState<PublicationDraft[]>(
        () =>
            publications?.map(({ id, startDate, endDate }) => ({
                id,
                startDate: new Date(startDate),
                endDate: new Date(endDate),
            })) ?? (isNewGift ? [defaultPublicationDraft()] : []),
    );
    const [error, setError] = useState<string | null>(null);
    const [savingId, setSavingId] = useState<string>();
    const [savingNew, setSavingNew] = useState(false);

    const updateDraft = (index: number, patch: Partial<PublicationDraft>) =>
        setDrafts((current) =>
            current.map((item, i) =>
                i === index ? { ...item, ...patch } : item,
            ),
        );

    const validWindow = (draft: PublicationDraft) => {
        const window = toWindow(draft);
        if (!window) return undefined;
        if (new Date(window.endDate) <= new Date(window.startDate)) {
            setError('End date must be after start date.');
            return undefined;
        }
        setError(null);
        return window;
    };

    const add = () => {
        setError(null);
        setDrafts((current) => [...current, defaultPublicationDraft()]);
    };

    const discard = (index: number) =>
        setDrafts((current) => current.filter((_, i) => i !== index));

    const create = async (index: number) => {
        const draft = drafts[index];
        if (!draft || !onCreatePublication) return;
        const window = validWindow(draft);
        if (!window) return;
        setSavingNew(true);
        try {
            const created = await onCreatePublication(window);
            updateDraft(index, {
                id: created.id,
                startDate: new Date(created.startDate),
                endDate: new Date(created.endDate),
            });
        } catch {
            setError('Could not add publication.');
        } finally {
            setSavingNew(false);
        }
    };

    const update = async (index: number) => {
        const draft = drafts[index];
        if (!draft?.id || !onUpdatePublication) return;
        const window = validWindow(draft);
        if (!window) return;
        setSavingId(draft.id);
        try {
            await onUpdatePublication(draft.id, window);
        } catch {
            setError('Could not update publication.');
        } finally {
            setSavingId(undefined);
        }
    };

    const remove = async (publicationId: string) => {
        try {
            await onDeletePublication?.(publicationId);
            setDrafts((current) =>
                current.filter((item) => item.id !== publicationId),
            );
        } catch {
            setError('Could not remove publication.');
        }
    };

    return {
        drafts,
        error,
        savingId,
        savingNew,
        updateDraft,
        add,
        discard,
        create,
        update,
        remove,
    };
}

export type PublicationDraftsState = ReturnType<typeof usePublicationDrafts>;
